/**
 * Trade policy panel — trade restrictions and policy changes.
 *
 * Filters events from source 'trade', type 'economic' with trade keywords,
 * or events tagged 'trade'/'tariff'. Dropdown for restriction type.
 * Click → emit event:selected for detail view.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { relativeTime } from '@/utils/time';
import { SEVERITY_CSS_COLORS } from '@/utils/severity';
import type { NormalizedEvent, Severity } from '@/sources/base-source';

type RestrictionType = 'all' | 'tariff' | 'sanction' | 'embargo' | 'export-control' | 'other';

const RESTRICTION_OPTIONS: { label: string; value: RestrictionType }[] = [
  { label: 'All', value: 'all' },
  { label: 'Tariff', value: 'tariff' },
  { label: 'Sanction', value: 'sanction' },
  { label: 'Embargo', value: 'embargo' },
  { label: 'Export Control', value: 'export-control' },
  { label: 'Other', value: 'other' },
];

const TRADE_KEYWORDS = ['tariff', 'sanction', 'embargo', 'trade war', 'quota', 'duty', 'trade restriction', 'trade policy', 'trade deal', 'trade agreement', 'export control', 'import ban'];

function isTradeEvent(ev: NormalizedEvent): boolean {
  if (ev.source === 'trade') return true;
  if (ev.tags?.some(t => t === 'trade' || t === 'tariff' || t === 'sanction' || t === 'embargo')) return true;
  if (ev.type === 'economic' || ev.type === 'sanction') {
    const text = (ev.title + ' ' + (ev.description ?? '')).toLowerCase();
    return TRADE_KEYWORDS.some(kw => text.includes(kw));
  }
  return false;
}

function inferRestrictionType(ev: NormalizedEvent): RestrictionType {
  const tags = ev.tags ?? [];
  const text = (ev.title + ' ' + (ev.description ?? '')).toLowerCase();

  for (const tag of tags) {
    if (tag === 'tariff') return 'tariff';
    if (tag === 'sanction') return 'sanction';
    if (tag === 'embargo') return 'embargo';
    if (tag === 'export-control') return 'export-control';
  }

  if (text.includes('tariff') || text.includes('duty') || text.includes('quota')) return 'tariff';
  if (text.includes('sanction')) return 'sanction';
  if (text.includes('embargo')) return 'embargo';
  if (text.includes('export control') || text.includes('export ban') || text.includes('export restriction')) return 'export-control';
  return 'other';
}

export function createTradePolicyPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'trade-policy-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  // Filter bar
  const filterBar = document.createElement('div');
  filterBar.style.cssText = `
    display: flex; align-items: center; gap: 8px; padding: 8px 12px;
    border-bottom: 1px solid var(--border-primary); flex-shrink: 0;
  `;

  const filterLabel = document.createElement('span');
  filterLabel.style.cssText = 'font-size: 11px; color: var(--text-secondary);';
  filterLabel.textContent = 'Type:';
  filterBar.appendChild(filterLabel);

  const select = document.createElement('select');
  select.style.cssText = `
    font-size: 11px; padding: 3px 8px; border-radius: 4px;
    background: var(--bg-secondary); color: var(--text-primary);
    border: 1px solid var(--border-primary); cursor: pointer;
  `;

  for (const opt of RESTRICTION_OPTIONS) {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    select.appendChild(option);
  }

  filterBar.appendChild(select);
  container.appendChild(filterBar);

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px; flex: 1; overflow-y: auto;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;
  let activeFilter: RestrictionType = 'all';

  select.onchange = () => {
    activeFilter = select.value as RestrictionType;
    scheduleRender();
  };

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function render(): void {
    const events = appStore.getState().events;
    let tradeEvents = events
      .filter(isTradeEvent)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    if (activeFilter !== 'all') {
      tradeEvents = tradeEvents.filter(e => inferRestrictionType(e) === activeFilter);
    }

    contentArea.textContent = '';

    // Count header
    const countHeader = document.createElement('div');
    countHeader.style.cssText = `
      display: flex; align-items: center; gap: 8px; margin-bottom: 12px;
      padding: 8px 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    const countNum = document.createElement('span');
    countNum.style.cssText = `font-size: 20px; font-weight: 700; font-family: var(--font-mono); color: ${tradeEvents.length > 0 ? 'var(--severity-medium)' : 'var(--text-muted)'};`;
    countNum.textContent = String(tradeEvents.length);
    countHeader.appendChild(countNum);

    const countLabel = document.createElement('span');
    countLabel.style.cssText = 'font-size: 11px; color: var(--text-secondary);';
    countLabel.textContent = `trade policy event${tradeEvents.length !== 1 ? 's' : ''}`;
    countHeader.appendChild(countLabel);

    contentArea.appendChild(countHeader);

    if (tradeEvents.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'No recent trade policy events';
      contentArea.appendChild(msg);
      return;
    }

    for (const ev of tradeEvents.slice(0, 60)) {
      const sev = ev.severity ?? 'info';
      const row = document.createElement('div');
      row.style.cssText = `
        padding: 8px; margin-bottom: 4px; border-radius: 4px;
        background: var(--bg-secondary);
        border-left: 3px solid ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
        cursor: pointer;
      `;

      row.onclick = () => eventBus.emit('event:selected', ev);
      row.title = 'Click for details';

      // Title
      const titleEl = document.createElement('div');
      titleEl.style.cssText = 'font-size: 12px; font-weight: 500; color: var(--text-primary); margin-bottom: 4px;';
      titleEl.textContent = ev.title;
      row.appendChild(titleEl);

      // Detail row: restriction type, affected countries, severity, timestamp
      const detailRow = document.createElement('div');
      detailRow.style.cssText = 'display: flex; align-items: center; gap: 6px; flex-wrap: wrap;';

      // Restriction type badge
      const typeBadge = document.createElement('span');
      const rType = inferRestrictionType(ev);
      typeBadge.style.cssText = `
        font-size: 9px; font-weight: 600; padding: 1px 5px; border-radius: 3px;
        background: var(--bg-primary); color: var(--text-muted);
        text-transform: uppercase;
      `;
      typeBadge.textContent = rType.replace('-', ' ');
      detailRow.appendChild(typeBadge);

      // Affected countries from entities or location
      const countries: string[] = [];
      if (ev.location?.country) countries.push(ev.location.country);
      if (ev.entities) {
        for (const ent of ev.entities.slice(0, 3)) {
          if (ent.type === 'country' || ent.type === 'GPE') countries.push(ent.name);
        }
      }
      if (countries.length > 0) {
        const countriesEl = document.createElement('span');
        countriesEl.style.cssText = 'font-size: 10px; color: var(--text-secondary);';
        countriesEl.textContent = [...new Set(countries)].join(', ');
        detailRow.appendChild(countriesEl);
      }

      // Severity
      const sevBadge = document.createElement('span');
      sevBadge.style.cssText = `
        font-size: 9px; font-weight: 600; padding: 1px 6px; border-radius: 3px;
        text-transform: uppercase; margin-left: auto;
        background: ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'}22;
        color: ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
      `;
      sevBadge.textContent = sev;
      detailRow.appendChild(sevBadge);

      row.appendChild(detailRow);

      // Timestamp
      const time = document.createElement('div');
      time.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 3px;';
      time.textContent = relativeTime(ev.timestamp);
      row.appendChild(time);

      contentArea.appendChild(row);
    }
  }

  render();

  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
